import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Copy, FileImage, FileText, FileSpreadsheet, File as FileIcon, CloudUpload, FolderPlus, Folder, FolderOpen, X, Download } from "lucide-react";
import { toast } from "sonner";
import { API, backendUrl, imgUrl, fmtDate } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fileKind = (f) => {
  const ct = f.content_type || "";
  const name = (f.original_filename || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (ct.includes("sheet") || ct.includes("excel") || /\.(xlsx?|csv)$/.test(name)) return "excel";
  return "other";
};

const kindIcon = (kind) =>
  kind === "image" ? FileImage : kind === "pdf" ? FileText : kind === "excel" ? FileSpreadsheet : FileIcon;

export default function Media() {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState(["General"]);
  const [activeFolder, setActiveFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const dirRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: fs }, { data: fds }] = await Promise.all([API.get("/files"), API.get("/folders")]);
    setFiles(fs);
    setFolders(fds.length ? fds : ["General"]);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const uploadOne = async (file, folder) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder || "General");
    await API.post("/upload", fd);
  };

  const uploadFiles = async (list, folder) => {
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) await uploadOne(f, folder);
      toast.success(`${list.length} file(s) uploaded to "${folder || "General"}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (dirRef.current) dirRef.current.value = "";
    }
  };

  const uploadDirectory = (list) => {
    const arr = Array.from(list);
    const first = arr[0]?.webkitRelativePath || "";
    const folder = first.includes("/") ? first.split("/")[0] : activeFolder || "General";
    uploadFiles(arr, folder);
  };

  const createFolder = () => {
    const name = folderName.trim();
    if (!name) return toast.error("Folder name is required");
    setFolders((p) => (p.includes(name) ? p : [...p, name].sort()));
    setActiveFolder(name);
    setFolderOpen(false);
    setFolderName("");
    toast.success(`Folder "${name}" ready — upload files into it`);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(`${backendUrl}${url}`);
    toast.success("Link copied");
  };

  const remove = async (id) => {
    await API.delete(`/files/${id}`);
    toast.success("File deleted");
    setPreview(null);
    load();
  };

  const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const visible = activeFolder ? files.filter((f) => (f.folder || "General") === activeFolder) : files;

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Media Library</h1>
          <p className="text-sm text-slate-500">Images, PDFs, Excel & more — organized in folders, stored in the cloud</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files), activeFolder || "General")} data-testid="media-upload-input" />
          <input ref={dirRef} type="file" webkitdirectory="" className="hidden" onChange={(e) => uploadDirectory(e.target.files)} data-testid="media-folder-upload-input" />
          <Button variant="outline" onClick={() => setFolderOpen(true)} data-testid="new-folder-button">
            <FolderPlus className="mr-2 h-4 w-4" /> New Folder
          </Button>
          <Button variant="outline" onClick={() => dirRef.current?.click()} disabled={uploading} data-testid="media-upload-folder-button">
            <Folder className="mr-2 h-4 w-4" /> Upload Folder
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button">
            <CloudUpload className="mr-2 h-4 w-4" /> {uploading ? "Uploading..." : "Upload Files"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="folder-chips">
        <button
          onClick={() => setActiveFolder("")}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${!activeFolder ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300"}`}
          data-testid="folder-chip-all"
        >
          <FolderOpen className="h-3.5 w-3.5" /> All Files
        </button>
        {folders.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFolder(f)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${activeFolder === f ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300"}`}
            data-testid={`folder-chip-${f}`}
          >
            <Folder className="h-3.5 w-3.5" /> {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="media-grid">
        {visible.map((f) => {
          const kind = fileKind(f);
          const Icon = kindIcon(kind);
          return (
            <div key={f.id} className="group cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-white transition-shadow duration-200 hover:shadow-md" onClick={() => setPreview(f)} data-testid={`media-card-${f.id}`}>
              <div className="flex h-36 items-center justify-center bg-slate-50">
                {kind === "image" ? (
                  <img src={imgUrl(f.url)} alt={f.original_filename} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-slate-400">
                    <Icon className="h-10 w-10" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">{kind}</span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 truncate text-sm font-medium text-slate-900">
                  <Icon className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
                  <span className="truncate">{f.original_filename || "file"}</span>
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{kb(f.size || 0)} · {f.folder || "General"} · {fmtDate(f.created_at)}</div>
                <div className="mt-2 flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" onClick={() => copyLink(f.url)} data-testid={`media-copy-${f.id}`}>
                    <Copy className="mr-1 h-3 w-3" /> Copy Link
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(f.id)} data-testid={`media-delete-${f.id}`} aria-label="Delete file">
                    <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {visible.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-slate-300 p-10" data-testid="media-empty">
          <Plus className="h-6 w-6 text-slate-400" />
          <EmptyState title="No files here yet" hint="Upload images, PDFs or Excel sheets into this folder." />
        </div>
      )}

      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent data-testid="new-folder-dialog">
          <DialogHeader><DialogTitle>New Folder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Folder Name *</Label>
              <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="e.g. Product Catalogs, Price Lists" className="mt-1.5" data-testid="folder-name-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={createFolder} data-testid="create-folder-submit">Create Folder</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="media-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8 text-base">
                  {preview.original_filename}
                  <span className="text-xs font-normal text-slate-400">{kb(preview.size || 0)} · {preview.folder || "General"}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="flex max-h-[70vh] items-center justify-center overflow-auto rounded-md bg-slate-50" data-testid="media-preview-body">
                {fileKind(preview) === "image" ? (
                  <img src={imgUrl(preview.url)} alt={preview.original_filename} className="max-h-[70vh] w-auto object-contain" />
                ) : fileKind(preview) === "pdf" ? (
                  <iframe src={imgUrl(preview.url)} title={preview.original_filename} className="h-[70vh] w-full" />
                ) : (
                  <div className="flex flex-col items-center gap-3 p-16 text-slate-500">
                    {(() => { const I = kindIcon(fileKind(preview)); return <I className="h-16 w-16 text-slate-300" />; })()}
                    <div className="text-sm">Preview not available for this file type.</div>
                    <Button variant="outline" onClick={() => window.open(imgUrl(preview.url), "_blank")} data-testid="preview-download-button">
                      <Download className="mr-2 h-4 w-4" /> Download / Open
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={() => copyLink(preview.url)} data-testid="preview-copy-button">
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy Link
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPreview(null)} data-testid="preview-close-button">
                  <X className="mr-1 h-3.5 w-3.5" /> Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
